import { ListingsSearchBarConnected } from '@components/listings-search-bar';
import { useIsImproveMpPerformanceTier2Enabled } from '@hooks/use-improve-mp-performance-tier-2';
import type { Match } from '@hooks/use-search-by-bounding-box';
import { useBrowserNavigationSync } from '@hooks/useBrowserNavigationSync';
import useIsSticky from '@hooks/useIsSticky';
import {
	CATEGORY_FILTER_ID,
	appliedFacetsForImpressionsAtom,
	appliedFiltersCountAtom,
	browserNavigationUrlAtom,
	classifierSyncAtom,
	filterConfigsAtom,
	filterValuesAtom,
	selectedCategoryCodesAtom,
	seoFacetIdsAtom,
	urlFilterParamsAtom,
} from '@pages/VendorsSearch/containers/FilterPills/atoms';
import { searchScrolledOutAtom } from '@pages/VendorsSearch/containers/SemanticSearch/atoms';
import { setRequestInfo } from '@redux/request/actionCreators';
import * as VendorImpressionThunks from '@redux/vendorImpressions/thunks';
import { selectIsMobile } from '@redux/viewport/selectors';
import { GridRow } from '@tkww/orion-web-ds-structure';
import { compose } from '@xo-union/react-css-modules';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import React, { type FC, useEffect, useRef } from 'react';
import { connect, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { animateScroll } from 'react-scroll';
import { seoFilter } from '../../../../../helpers/seo';
import * as SearchActions from '../../../../redux/search/actions';
import * as SeoActions from '../../actions/seo';
import PillSearch from './components/PillSearch';
import FilterSidebar from './components/Sidebar';
import AppliedFilters from './containers/AppliedFilters';
import FilterPills from './containers/FilterPills';
import { createClassForPillRow, updateHistory } from './helpers';
import { useCategoryFilterSync } from './hooks/use-category-filter-sync';
import Styles from './styles.scss';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

interface MapViewButtonProps {
	match: Match;
	searchLocation: string;
	showMapView: boolean;
}

export type Props = StateProps & DispatchProps & MapViewButtonProps;

/**
 * Total pills (including "+ More filters") shown in the sticky row when the
 * condensed search bar is present and the viewport is below `lg`, so the bar
 * keeps its 312px floor instead of being clipped. Measured, not from the
 * designs: 4 pills overflow the row by 64px at 768px. See GS-1253.
 */
const CONDENSED_SEARCH_PILL_LIMIT = 3;

const FilterPillsWrapper: FC<Props> = ({
	categoryCode,
	filterPillLimit,
	receiveFilterPills,
	sort,
	setFilterPillLimit,
	transmitImpressions,
	updateSeoFilterValue,
	viewport,
	match = { params: { facet: '', locationSlug: '' } },
	searchLocation,
	showMapView,
	requestInfo,
	setRequestInfo,
}) => {
	const location = useLocation();
	const { pathname, search } = location;
	const { isMobile, lessThan, greaterThan } = viewport;
	const history = useHistory();
	const urlFilterParams = useAtomValue(urlFilterParamsAtom);
	const jotaiStore = useStore();
	const stickyRef = useRef(null);
	const isImproveMpPerformanceTier2Enabled =
		useIsImproveMpPerformanceTier2Enabled();
	const isSticky = useIsSticky(
		stickyRef,
		isMobile && !isImproveMpPerformanceTier2Enabled,
	);
	const geolocation = useSelector((state: Redux.State) => state.location);
	// Once the header has scrolled out, a condensed search bar joins the sticky
	// chips. Gate on `selectIsMobile`, not the raw `viewport.isMobile` prop, which
	// only reflects the server-detected user agent — a narrowed desktop browser
	// reports false there and would render the bar into a layout where `@desktop`
	// styles don't apply. It's also the selector `SemanticTopBar` uses, so the
	// header and this row can't disagree about which layout is active.
	const isMobileLayout = useSelector(selectIsMobile);
	const isSearchScrolledOut = useAtomValue(searchScrolledOutAtom);
	const shouldShowCondensedSearch = isSearchScrolledOut && !isMobileLayout;
	// Mobile collapses to a "Search" button beside "Filters" rather than embedding
	// the search field, per the `mWeb / Listings` design (GS-1187) — a separate
	// affordance from `shouldShowCondensedSearch`, mutually exclusive with it.
	const shouldShowMobileSearchPill = isSearchScrolledOut && isMobileLayout;
	const appliedFiltersCount = useAtomValue(appliedFiltersCountAtom);
	const seoFacetIds = useAtomValue(seoFacetIdsAtom);
	const appliedFacetsForImpressions = useAtomValue(
		appliedFacetsForImpressionsAtom,
	);
	const categoryCodes = useAtomValue(selectedCategoryCodesAtom);
	const filterConfigs = useAtomValue(filterConfigsAtom);
	const shouldAddCategoryFilterPill = CATEGORY_FILTER_ID in filterConfigs;
	const setFilterValues = useSetAtom(filterValuesAtom);
	const isFirstRender = useRef(true);

	// Handle browser back/forward navigation with filter state sync
	// See useBrowserNavigationSync.ts for detailed documentation on why this is needed
	const { updateLastProcessedUrl } = useBrowserNavigationSync({
		filterConfigs,
		categoryCode,
		setFilterValues,
		setBrowserNavigationUrl: (url) => {
			jotaiStore.set(browserNavigationUrlAtom, url);
		},
	});

	// Dynamically load category-specific filters when 1 category is selected,
	// and reset to universal filters for 0 or 2+ selected categories.
	useCategoryFilterSync();

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return; // Skip the effect on first render
		}

		const currentUrl = pathname + search;

		transmitImpressions({
			transmitMinimumLimit: 1,
			location: geolocation,
			appliedFilters: appliedFacetsForImpressions,
			categoryCodes,
		});

		const transformedFilters = Object.fromEntries(
			urlFilterParams.map(({ key, value }) => [
				key,
				value.includes('+') ? value.split('+') : value,
			]),
		);

		setRequestInfo({
			...requestInfo,
			queryParams: {
				...requestInfo.queryParams,
				...transformedFilters,
				sort,
			},
		});

		// When the classifier is syncing categories it manages the URL directly via
		// history.replace. Skip the normal history.push here to avoid overwriting
		// the classifier's URL update. Still update requestInfo (above) and the
		// tracked URL so back/forward navigation stays correct.
		if (jotaiStore.get(classifierSyncAtom)) {
			jotaiStore.set(classifierSyncAtom, false);
			updateLastProcessedUrl();
			return;
		}

		// When this effect is running for a browser back/forward URL we just synced,
		// skip updateHistory exactly once for that URL.
		const browserNavigationUrl = jotaiStore.get(browserNavigationUrlAtom);
		if (browserNavigationUrl === currentUrl) {
			jotaiStore.set(browserNavigationUrlAtom, null);
			updateLastProcessedUrl();
			return;
		}

		updateHistory({
			urlFilterParams,
			pathname,
			search,
			sort,
			history,
			locationSlug: match?.params?.locationSlug,
			categoryFilterConfig: filterConfigs[CATEGORY_FILTER_ID],
		});

		// Update tracked URL after programmatic changes to prevent race condition
		// This ensures browser back/forward navigation is properly detected
		updateLastProcessedUrl();

		animateScroll.scrollToTop();
	}, [urlFilterParams]);

	useEffect(() => {
		if (isSticky || showMapView) {
			document.body.classList.add(Styles.stickyBody);
		} else {
			document.body.classList.remove(Styles.stickyBody);
		}

		return () => document.body.classList.remove(Styles.stickyBody);
	}, [isSticky, showMapView]);

	useEffect(() => {
		// When the Category filter is present, use an empty code to avoid matching
		// a category-specific pill dictionary — pills should come from filterConfigs.
		const pillCategoryCode = shouldAddCategoryFilterPill
			? ('' as typeof categoryCode)
			: categoryCode;

		// Switching from desktop to mobile
		if (isMobile || lessThan.small) {
			receiveFilterPills({
				categoryCode: pillCategoryCode,
				filterConfigs,
				mobileUI: true,
				shouldAddCategoryFilterPill,
			});
			return;
		}

		// Below `lg` there isn't room for the usual pill count alongside the condensed
		// bar's 312px floor, so the surplus pills move into "+ More filters" (GS-1253).
		if (shouldShowCondensedSearch && !greaterThan.medium) {
			if (filterPillLimit !== CONDENSED_SEARCH_PILL_LIMIT) {
				receiveFilterPills({
					categoryCode: pillCategoryCode,
					filterConfigs,
					mobileUI: false,
					desktopLimit: CONDENSED_SEARCH_PILL_LIMIT,
					shouldAddCategoryFilterPill,
				});
			}
			return;
		}

		// Switching from mobile to small desktop, or back from the reduced count
		// above. One comparison covers both — the mobile limit and
		// CONDENSED_SEARCH_PILL_LIMIT are both 3 and need the same reset.
		if (filterPillLimit === 3) {
			receiveFilterPills({
				categoryCode: pillCategoryCode,
				filterConfigs,
				mobileUI: false,
				desktopLimit: 5,
				shouldAddCategoryFilterPill,
			});
			return;
		}

		// Switching from small desktop to large desktop
		if (greaterThan.large) {
			setFilterPillLimit(6);
			return;
		}

		// Switching from large desktop to small desktop
		setFilterPillLimit(5);
	}, [viewport, shouldShowCondensedSearch]);

	useEffect(() => {
		const updatedSeoFilter = seoFilter(
			categoryCode,
			seoFacetIds,
			false,
			match.params.facet || undefined,
		);
		updateSeoFilterValue(updatedSeoFilter);
	}, [seoFacetIds, categoryCode, match.params.facet, updateSeoFilterValue]);

	return (
		<>
			<div
				ref={stickyRef}
				className={createClassForPillRow(isSticky, showMapView)}
				// Drives the gutter correction in styles.scss that lets the condensed
				// bar reach the same right edge as the header's search bar.
				data-condensed-search={shouldShowCondensedSearch}
			>
				<FilterPills
					match={match}
					searchLocation={searchLocation}
					showMapView={showMapView}
					isSticky={isSticky}
					// Passed down rather than rendered here — see the prop's doc comment.
					condensedSearch={
						shouldShowCondensedSearch ? (
							<ListingsSearchBarConnected />
						) : undefined
					}
					mobileSearchPill={
						shouldShowMobileSearchPill ? <PillSearch /> : undefined
					}
				/>
			</div>

			<GridRow
				classes={compose({
					row: appliedFiltersCount
						? Styles.container
						: Styles.containerNoAppliedFilters,
				})}
			>
				<AppliedFilters />
			</GridRow>
			<FilterSidebar />
		</>
	);
};

const mapStateToProps = (state: Redux.State) => ({
	categoryCode: state.category.code,
	filterPillLimit: state.search.filterPills.limit,
	sort: state.search.sort,
	viewport: state.viewport,
	vendorImpressions: state.vendorImpressions,
	requestInfo: state.requestInfo,
});

const mapDispatchToProps = {
	receiveFilterPills: SearchActions.receiveFilterPills,
	setFilterPillLimit: SearchActions.setFilterPillLimit,
	transmitImpressions: VendorImpressionThunks.transmitImpressions,
	updateSeoFilterValue: SeoActions.updateSeoFilterValue,
	setRequestInfo: setRequestInfo,
};

export default connect(mapStateToProps, mapDispatchToProps)(FilterPillsWrapper);
